import React from 'react';
import { Check, Settings } from 'lucide-react';
import type { BilanImport } from '../../../lib/planningExcelImport/types';

/**
 * Le récapitulatif de fin d'import (maquette 26c) — ce qui a été créé, et ce qui reste à faire à la main.
 *
 * Les codes créés y sont listés avec un lien vers les réglages : c'est là qu'on dit si un code est
 * rémunéré, décompté des congés, ou simplement informatif. Par défaut, ni l'un ni l'autre.
 */
export function RecapImport({ bilan, onOuvrirReglages }: {
    bilan: BilanImport;
    onOuvrirReglages: () => void;
}) {
    // Les renforts créés à l'étape 3 ne sont plus « à ressaisir » : seuls les autres restent listés.
    const crees = new Set(bilan.renfortsCrees || []);
    const aRessaisir = bilan.renforts.filter((_, index) => !crees.has(index));

    return (
        <div className="flex flex-col gap-[13px]">
            <div className="flex items-center gap-[11px] px-[13px] py-[11px] rounded-[10px] bg-state-ok-soft border border-state-ok/30">
                <span className="w-7 h-7 shrink-0 rounded-[7px] bg-surface border border-state-ok/30 text-state-ok flex items-center justify-center">
                    <Check size={14} />
                </span>
                <div className="min-w-0 text-state-ok">
                    <div className="text-[12.5px] font-semibold">
                        {bilan.creneaux} créneaux et {bilan.absences} absences écrits
                        {crees.size > 0 ? `, ${crees.size} renfort${crees.size > 1 ? 's' : ''} créé${crees.size > 1 ? 's' : ''}` : ''}
                    </div>
                    <div className="text-[11.5px] opacity-80">
                        dont {bilan.creneauxDeNuit} créneaux terminant après minuit
                    </div>
                </div>
            </div>

            {bilan.codesCrees.length > 0 && (
                <div className="border border-border rounded-[10px] overflow-hidden">
                    <div className="grid grid-cols-[62px_minmax(0,1fr)_96px] px-[13px] py-2 bg-surface-soft border-b border-border">
                        <span className="text-[9.5px] font-bold uppercase tracking-[.05em] text-content-muted">Code</span>
                        <span className="text-[9.5px] font-bold uppercase tracking-[.05em] text-content-muted">Devient</span>
                        <span className="text-[9.5px] font-bold uppercase tracking-[.05em] text-content-muted text-right">État</span>
                    </div>
                    <div className="max-h-[200px] overflow-y-auto">
                        {bilan.codesCrees.map((code) => (
                            <div key={code.code} className="grid grid-cols-[62px_minmax(0,1fr)_96px] items-center px-[13px] py-[9px] border-b border-border/60 last:border-b-0">
                                <span className="font-mono text-[11.5px] font-medium text-content">{code.code}</span>
                                <span className="text-xs min-w-0 truncate text-content-muted">{code.libelle || code.code}</span>
                                <span className="flex justify-end">
                                    <span className="inline-flex items-center h-[21px] px-2 rounded-full bg-state-warn-soft border border-state-warn/30 text-state-warn text-[10px] font-bold whitespace-nowrap">
                                        créé
                                    </span>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {bilan.codesCrees.length > 0 && (
                <button
                    type="button"
                    onClick={onOuvrirReglages}
                    className="flex items-center gap-2 self-start text-[12px] font-semibold text-content-muted hover:text-content underline"
                >
                    <Settings size={13} /> Qualifier ces codes dans les réglages
                </button>
            )}

            {aRessaisir.length > 0 && (
                <div className="border border-border rounded-[10px] overflow-hidden">
                    <div className="px-[13px] py-2 bg-surface-soft border-b border-border">
                        <span className="text-[9.5px] font-bold uppercase tracking-[.05em] text-content-muted">
                            Renforts extérieurs non créés, à ressaisir si besoin ({aRessaisir.length})
                        </span>
                    </div>
                    <div className="max-h-[180px] overflow-y-auto">
                        {aRessaisir.map((renfort, index) => (
                            <div key={`${renfort.date}-${index}`} className="flex items-center gap-2 px-[13px] py-2 border-b border-border/60 last:border-b-0">
                                <span className="font-mono text-[11px] text-content-muted shrink-0">{renfort.date}</span>
                                <span className="text-xs min-w-0 truncate text-content">{renfort.texte}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {bilan.nomsIgnores.length > 0 && (
                <div className="px-[13px] py-3 rounded-[10px] bg-surface-soft border border-border">
                    <div className="text-[11.5px] font-semibold text-content mb-1">
                        {bilan.nomsIgnores.length} lignes laissées de côté
                    </div>
                    <p className="text-[11.5px] leading-relaxed text-content-muted text-pretty">
                        {bilan.nomsIgnores.join(' · ')}
                    </p>
                </div>
            )}
        </div>
    );
}
